import React, { useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getSingleProduct,
  updateProduct,
} from "../../../../redux/Product/action";
import { addProductValidation } from "../../../Constant/constant";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import { setGetSubcategories } from "../../../../reusableSlice";
import ProductFormShell from "./form/ProductFormShell";
import { FormOptionsContext } from "./form/FormOptionsContext";
import { useProductFormOptions } from "./form/useProductFormOptions";
import { mapProductToFormValues, mapLinkedProductOptions } from "./form/values";
import { BarcodeFieldError, normalizeBarcode } from "./form/barcode";
import { stripUnpermittedPriceFields } from "./form/payload";
import "./Product.css";

const UpdateProduct = () => {
  const { id } = useParams();
  const productId = id ? Number(id) : undefined;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // The products list stashes its URL (page + filters) in navigation state when
  // it sends us here; return to it after save so the list is restored exactly.
  // Captured once on mount: the nested index route immediately redirects
  // /updateproduct/:id -> .../general via <Navigate replace>, which wipes
  // location.state — reading it lazily here preserves it across that redirect.
  const [returnTo] = useState(() => location.state?.from);
  const [loading, setLoading] = useState(false);
  const { hasAction } = usePermissions();
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);

  useEffect(() => {
    if (productId) dispatch(getSingleProduct(productId));
  }, [dispatch, productId]);

  useEffect(() => {
    return () => {
      dispatch(setGetSubcategories([]));
    };
  }, [dispatch]);

  const singleProduct = useSelector((s) => s.reusableSlice.singleProduct);

  const initialValues = useMemo(
    () => mapProductToFormValues(singleProduct),
    [singleProduct],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: addProductValidation({
      canSeeCost: canEditCost,
      canSeeRegularPrice: canEditRegularPrice,
      canSeeSalePrice: canEditSalePrice,
    }),
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      const base = stripUnpermittedPriceFields(values, {
        canEditCost,
        canEditRegularPrice,
        canEditSalePrice,
      });
      const payload = {
        ...base,
        barcode: normalizeBarcode(values.barcode),
        id: singleProduct?.id,
      };
      try {
        await dispatch(
          updateProduct(payload, setLoading, (to) =>
            navigate(returnTo || to, { replace: true })
          )
        );
      } catch (err) {
        if (err instanceof BarcodeFieldError) {
          setFieldError("barcode", err.fieldMessage);
          setFieldTouched("barcode", true, false);
          return;
        }
        // swal already shown; swallow so Formik resets isSubmitting
      }
    },
  });

  const options = useProductFormOptions({
    selectedCategoryIds: formik.values.category_ids,
    selectedSubCategoryIds: formik.values.sub_category_ids,
    excludeProductId: productId,
  });

  // Display data for the already-linked siblings, taken from this product's own
  // record. Lets the Product Options chips render the real name even when the
  // linked product isn't on page 1 of the global list (which was showing
  // "Product #<id>" before).
  const linkedProductOptions = useMemo(
    () => mapLinkedProductOptions(singleProduct?.product_options),
    [singleProduct],
  );

  const optionsContextValue = useMemo(
    () => ({ ...options, linkedProductOptions }),
    [options, linkedProductOptions],
  );

  const basePath = `/updateproduct/${id}`;

  return (
    <FormikProvider value={formik}>
      <FormOptionsContext.Provider value={optionsContextValue}>
        <ProductFormShell
          mode="edit"
          title="Update Product"
          basePath={basePath}
          loading={loading}
          outletContext={{ singleProduct }}
        />
      </FormOptionsContext.Provider>
    </FormikProvider>
  );
};

export default UpdateProduct;
